import { all, put, select, takeLatest, call } from "redux-saga/effects";
import * as Actions from "../../actions/schedulingportal/technicianAppointment";
import * as api from "../../../api/schedulingPortal";
import moment from "moment";
import { max } from "lodash";
import {
  ASSIGNED_EID,
  ASSIGNED_TO,
  ASSIGNED_TO_EMAIL,
  CUSTOMER_PORTAL_INTEGRATION_USER_ID,
  DE_RE_VENDOR_QUE_ID,
  MODIFIED_BY_EID,
  RESCHEDULED,
  GOVT_ASSIGNED_EID,
  GOVT_MODIFIED_BY_EID,
  GOVT_ASSIGNED_TO_EMAIL,
  GOVT_REVEAL_SUPPORT_QUE_ID,
} from "../../../constants/technicianAppointmentConstants";
import VEHICLES_lIST from "../../../components/technicianAppointments/VehicleorAssetDetailsMock";
import { getLineIdForJobDurationDetails } from "../../../components/technicianAppointments/JobDurationDetails";
import {
  addressPayload,
  checkIfAddressIsUpdated,
  contactPayload,
  jobDetailsPayload,
  servicesArray,
  caseSubjectField,
} from "../../../components/technicianAppointments/common/JobsPayload";
import { CFST_PENDO_DEFAULT_LABEL, pendoTrack } from "../../../util/pendoUtil";
import { getPageLabel } from "../../../util/cfstPendoUtils";
import { STAGEUSDOMAIN, QADOMAIN } from "../../../../src/constants/appConstants";

const isEnvUAT = () => {
  return STAGEUSDOMAIN.includes(window.location.hostname);
};

const isEnvQA = () => {
  return QADOMAIN.includes(window.location.hostname);
};

// Mapping of respective appointment status against C360 status
const appointmentStatusMapping = (data) => {
  let changedData = data.map((item) => {
    let status = item.status ? item.status : item.value;
    switch (status) {
      case "Rescheduled":
      case "Cancelled":
        break;
      case "PO Released":
      case "PO Released-All":
      case "PO Released-Partial":
        status = "Scheduled";
        break;
      case "Complete":
      case "Completed":
      case "Partially Completed":
        status = "Complete";
        break;
      case "Pending":
      case "OPEN":
        status = "Pending";
        break;
    }
    if (item.hasOwnProperty("status")) {
      item.status = status;
    } else {
      item.value = status;
    }
    return item;
  });
  return changedData;
};

function* fetchFee() {
  try {
    yield put(Actions.setLoader(true));
    let state = yield select((state) => state);
    let payload = {
      accId: state.ACCOUNT.account_id,
      contractId: state.BILLING.contractId,
    };
    const response = yield api.fetchFee(payload);
    if (response?.data?.data?.length > 0) {
      yield put(Actions.setFeeDetails({ fee: response?.data?.data[0]?.deReCharge, currencyCode: response?.data?.data[0]?.currencyCode }));
    } else {
      yield put(Actions.setFeeDetails({ fee: "--", currencyCode: null }));
    }
  } catch {
    yield put(Actions.setFeeDetails({ fee: "--", currencyCode: null }));
  }
}

// Fetch account state and region ID
function* fetchRegionId() {
  const regionId = yield select((state) => state.ACCOUNT.accountUserTokenDetails.accountRegionId);
  return regionId;
}

function* fetchCountryCode() {
  const ccode = yield select((state) => state.ACCOUNT.accountUserTokenDetails?.ccode);
  return ccode;
}

//function to validate the vin
function* validateVin({ vin, callBack }) {
  const { accountNumber } = yield select((state) => state.ACCOUNT);
  try {
    yield put(Actions.setLoader(true));
    const regionId = yield call(fetchRegionId);
    const ccode = yield call(fetchCountryCode);
    const requestPayload = {
      region: regionId,
      country: ccode,
      vinList: [vin],
      lineOfBusiness: "",
    };
    const { data: { statusCode, data: vinData } = {} } = yield api.vinValidationCall(requestPayload);
    if (statusCode === "200" && vinData && !vinData.invalidVins) {
      pendoTrack(`${CFST_PENDO_DEFAULT_LABEL} | VIN validation api`, {
        status: "Success",
        statusCode: statusCode,
        accountId: accountNumber,
      });
      const { year, make, model } = vinData;
      callBack(year, make, model, true);
    } else {
      pendoTrack(`${CFST_PENDO_DEFAULT_LABEL} | VIN validation api`, {
        status: "Invalid VIN",
        statusCode: statusCode,
        accountId: accountNumber,
      });
      callBack(null, null, null, !vinData?.invalidVins?.length);
    }
  } catch (error) {
    pendoTrack(`${CFST_PENDO_DEFAULT_LABEL} | VIN validation api`, {
      status: "Failed",
      statusCode: error.response?.data?.statusCode || error.data?.statusCode || error.response?.status,
      accountId: accountNumber,
    });
    callBack(null, null, null, false, true);
    yield put(Actions.setLoader(false));
  }
}

//function to check the compatibility of 2 vins
function* checkVinCompatibility({ oldVin, newVin, accessories, boxName, callBack, toLoad }) {
  const { accountNumber } = yield select((state) => state.ACCOUNT);
  try {
    if (toLoad) {
      yield put(Actions.setLoader(true));
    }

    // Create simplified request payload for new backend API
    const requestPayload = {
      oldVin,
      newVin,
      accessories, // Raw accessories from frontend
      boxName,
    };

    const response = yield api.vinCompatibilityCall(requestPayload);
    const {
      data: { statusCode, data: { isCompatible, notCompatibleReason } = {} },
    } = response;

    if (statusCode === "200") {
      if (isCompatible) {
        pendoTrack(`${CFST_PENDO_DEFAULT_LABEL} | Compatibility api`, {
          status: "Success",
          statusCode: statusCode,
          accountId: accountNumber,
        });
      } else {
        pendoTrack(`${CFST_PENDO_DEFAULT_LABEL} | Compatibility api`, {
          status: notCompatibleReason,
          accountId: accountNumber,
        });
      }
      callBack(isCompatible, notCompatibleReason);
    } else {
      pendoTrack(`${CFST_PENDO_DEFAULT_LABEL} | Compatibility api`, {
        status: "API Error",
        statusCode: statusCode,
        accountId: accountNumber,
      });
      callBack(false, "Unable to check VIN compatibility due to API error");
    }
  } catch (error) {
    pendoTrack(`${CFST_PENDO_DEFAULT_LABEL} | Compatibility api`, {
      status: "Failed",
      statusCode: error.response?.data?.statusCode || error.data?.statusCode || error.response?.status,
      accountId: accountNumber,
    });
    callBack(false, "Unable to check vin compatibility due to an unexpected error", true);
  } finally {
    yield put(Actions.setLoader(false));
  }
}

function* fetchStates() {
  const { accountNumber } = yield select((state) => state.ACCOUNT);
  try {
    yield put(Actions.setLoader(true));
    yield put(Actions.setStates({ statesList: [], error: false }));
    const response = yield api.fetchStates();
    if (response?.data?.data?.length > 0) {
      yield put(Actions.setStates({ statesList: response.data.data.filter((item) => item?.countryCode?.toLowerCase() === "usa"), error: false }));
      pendoTrack(`${CFST_PENDO_DEFAULT_LABEL} | States api`, {
        status: "Success",
        statusCode: response.data.statusCode,
        accountId: accountNumber,
      });
    } else {
      yield put(Actions.setStates({ statesList: [], error: false }));
      pendoTrack(`${CFST_PENDO_DEFAULT_LABEL} | States api`, {
        status: "Empty data found",
        statusCode: response.data.statusCode,
        accountId: accountNumber,
      });
    }
    yield put(Actions.setLoader(false));
  } catch (error) {
    pendoTrack(`${CFST_PENDO_DEFAULT_LABEL} | States api`, {
      status: "Failed",
      statusCode: error.response?.data?.statusCode || error.data?.statusCode || error.response?.status,
      accountId: accountNumber,
    });
    yield put(Actions.setStates({ statesList: [], error: true }));
    yield put(Actions.setLoader(false));
  }
}

function getCaseSubjectText(transferCount, removalCount) {
  if (transferCount > 0 && removalCount > 0) {
    return `${transferCount} Deinstall / ${removalCount} Re-install`;
  }
  if (transferCount > 0) {
    return `${transferCount} Deinstall`;
  }
  if (removalCount > 0) {
    return `${removalCount} Re-install`;
  }
  return "";
}

function* createCaseDetails() {
  try {
    yield put(Actions.setLoader(true));
    let state = yield select((state) => state);
    const { account_id, emailAddress, firstName, surname } = state.ACCOUNT;
    let serviceDetails = state.TECHNICIANAPPOINTMENT.serviceDetails;
    let transferCount = serviceDetails.filter((item) => item.serviceType === "Transfer").length;
    let removalCount = serviceDetails.filter((item) => item.serviceType === "Removal").length;
    let subject = getCaseSubjectText(transferCount, removalCount);

    let requestBody = {};
    requestBody = {
      accountId: account_id,
      subject: subject,
      country: "US",
      status: "New",
      origin: "VZC-Customer Portal",
      reason: "Hardware",
      specificReason: "De/Re",
      caseOwner: CUSTOMER_PORTAL_INTEGRATION_USER_ID,
      emailId: emailAddress,
      firstName: firstName,
      lastName: surname,
      productFamily: "Reveal",
    };
    //set case subject in redux store, so that it can be used in updateCase api
    yield put(Actions.setCaseSubject(subject));
    const response = yield api.createNewCase(requestBody);
    if (response?.data?.data?.caseNumber) {
      yield put(Actions.setCaseNumber(response?.data?.data?.caseNumber));
    }
    if (response?.data?.data?.id) {
      yield put(Actions.setCaseSfdcId(response?.data?.data?.id));
    }
    yield put(Actions.setLoader(false));
  } catch {
    yield put(Actions.setLoader(false));
  }
}

function* contactDetails(payload) {
  try {
    let requestBody = {};
    let contactFormData = {
      //actual data
      firstName: payload.value.firstName,
      lastName: payload.value.lastName,
      mobileNumber: payload.value.mobileNumber,
      landlineNumber: payload.value.landlineNumber,
      email: payload.value.email,
      contactId: payload.value.contactID,
      contactType: payload.value.contactType,
    };
    yield put(Actions.setContactFormValues(contactFormData));
    if (payload.value.contactType && payload.value.contactType === "use-existing-contact") {
      yield put(Actions.setLoader(true));
      requestBody = {
        contactId: payload.value.contactID,
        email: payload.value.email,
        phone: payload.value.landlineNumber ? "+1 " + payload.value.landlineNumber.toString() : "",
        mobilePhone: payload.value.mobileNumber ? "+1 " + payload.value.mobileNumber.toString() : "",
      };
      yield api.updateExistingContact(requestBody);
      yield put(Actions.setLoader(false));
    }
    if (payload.value.contactType && payload.value.contactType === "add-new-contact") {
      yield put(Actions.setLoader(true));
      let state = yield select((state) => state);
      let accountId = state.ACCOUNT.account_id;
      requestBody = {
        accountId: accountId,
        firstName: payload.value.firstName,
        lastName: payload.value.lastName,
        email: payload.value.email,
        phone: payload.value.landlineNumber ? "+1 " + payload.value.landlineNumber.toString() : "",
        mobilePhone: payload.value.mobileNumber ? "+1 " + payload.value.mobileNumber.toString() : "",
      };
      yield api.addNewContact(requestBody);
      yield put(Actions.setLoader(false));
    }
  } catch {
    yield put(Actions.setLoader(false));
  }
}

function* fetchAccountDetails({ accountId }) {
  const { accountNumber } = yield select((state) => state.ACCOUNT);
  try {
    yield put(Actions.setLoader(true));
    const response = yield api.fetchAccountDetails(accountId);
    if (response?.data?.data?.salesOrg) {
      const accountDetails = response.data.data;
      yield put(Actions.setAccountDetails({ data: accountDetails, error: false }));
      const { isReschedule, vehicleAndAssetList, materialList } = yield select((state) => state.TECHNICIANAPPOINTMENT);
      if (!isReschedule && !vehicleAndAssetList.vehicles?.length) {
        yield call(fetchVehicleAndAssetList, { toLoad: false });
      }
      const salesOrg = accountDetails.salesOrg,
        vzcSegement = accountDetails.vzcSegement;
      if (!isReschedule && !materialList?.data?.length && salesOrg && vzcSegement) {
        let requestPayload = {
          salesOrg,
          // isGovernment: vzcSegement?.toLowerCase().includes("government") ? "YES" : "NO",
          isGovernment: "NO",
        };
        yield call(fetchMaterialList, { payload: requestPayload, toLoad: false });
      }
      pendoTrack(`${CFST_PENDO_DEFAULT_LABEL} | Account details api`, {
        status: "Success",
        statusCode: response.data.statusCode,
        accountId: accountNumber,
      });
    } else {
      pendoTrack(`${CFST_PENDO_DEFAULT_LABEL} | Account details api`, {
        status: "No salesOrg found",
        statusCode: response.data.statusCode,
        accountId: accountNumber,
      });
      yield put(Actions.setAccountDetails({ data: {}, error: true }));
    }
  } catch (error) {
    yield put(Actions.setAccountDetails({ data: {}, error: true }));

    pendoTrack(`${CFST_PENDO_DEFAULT_LABEL} | Account details api`, {
      status: "Failed",
      statusCode: error.response?.data?.statusCode || error.data?.statusCode || error.response?.status,
      accountId: accountNumber,
    });
  } finally {
    yield put(Actions.setLoader(false));
  }
}

function* fetchMaterialList({ payload, toLoad }) {
  const { accountNumber } = yield select((state) => state.ACCOUNT);
  try {
    if (toLoad) {
      yield put(Actions.setMaterialListSkeltonLoader(true));
    }
    const response = yield api.fetchMaterialList(payload);
    if (response?.data?.data?.length > 0) {
      yield put(Actions.setMaterialList({ data: response.data.data, error: false, skeltonLoader: false }));
      pendoTrack(`${CFST_PENDO_DEFAULT_LABEL} | Materials api`, {
        status: "Success",
        statusCode: response.data.statusCode,
        accountId: accountNumber,
      });
    } else {
      yield put(Actions.setMaterialList({ data: [], error: true, skeltonLoader: false }));
      pendoTrack(`${CFST_PENDO_DEFAULT_LABEL} | Materials api`, {
        status: "Empty data",
        statusCode: response.data.statusCode,
        accountId: accountNumber,
      });
    }
  } catch (error) {
    yield put(Actions.setMaterialList({ data: [], error: true, skeltonLoader: false }));
    pendoTrack(`${CFST_PENDO_DEFAULT_LABEL} | Materials api`, {
      status: "Failed",
      statusCode: error.response?.data?.statusCode || error.data?.statusCode || error.response?.status,
      accountId: accountNumber,
    });
  } finally {
    if (toLoad) {
      yield put(Actions.setMaterialListSkeltonLoader(false));
    }
  }
}
function* getAllAppointment({ callBack }) {
  yield put(Actions.setAllAppointments([]));
  let state = yield select((state) => state);
  let accountId = state.ACCOUNT.account_id;
  let accountNumber = state.ACCOUNT.accountNumber;
  callBack(true, false);
  let payload = { accountId: accountId };
  try {
    const responseData = yield api.getTechnicianAppointments(payload);
    if (responseData.data?.data?.length > 0) {
      let formattedData = yield call(appointmentStatusMapping, responseData.data.data);
      yield put(Actions.setAllAppointments(formattedData));
    }
    pendoTrack(`${CFST_PENDO_DEFAULT_LABEL} | Jobs api`, {
      status: "Success",
      statusCode: responseData.data.statusCode,
      accountId: accountNumber,
    });
    callBack(false, false);
  } catch (e) {
    pendoTrack(`${CFST_PENDO_DEFAULT_LABEL} | Jobs api`, {
      status: "Failed",
      statusCode: e.response?.data?.statusCode || e.data?.statusCode || e.response?.status,
      accountId: accountNumber,
    });
    callBack(false, true);
  }
}

function* technicianAppointmentValidateAddressData(payload) {
  const { accountNumber } = yield select((state) => state.ACCOUNT);
  try {
    yield put(Actions.setLoader(true));

    const responseData = yield api.newAddressValidation(payload.response);

    yield put(Actions.setAddressValidationResponse(responseData?.data?.data));

    if (responseData?.data?.statusCode === "200" && responseData?.data?.data) {
      yield put(Actions.addressValidationApiCheck(true));
      if (responseData?.data?.data?.locationFound) {
        if (
          responseData?.data?.data?.inputAddress?.addressLine1?.toLowerCase() !==
            responseData?.data?.data?.standardizedInfo?.standardizedAddress.addressLine1?.toLowerCase() ||
          responseData?.data?.data?.inputAddress?.addressLine2?.toLowerCase() !==
            responseData?.data?.data?.standardizedInfo?.standardizedAddress?.addressLine2?.toLowerCase() ||
          responseData.data?.data?.inputAddress?.city?.toLowerCase() !==
            responseData?.data?.data?.standardizedInfo?.standardizedAddress?.city?.toLowerCase() ||
          responseData?.data?.data?.inputAddress?.stateProvince?.toLowerCase() !==
            responseData?.data?.data?.standardizedInfo?.standardizedAddress?.stateProvince?.toLowerCase() ||
          responseData?.data?.data?.inputAddress?.country?.toLowerCase() !==
            responseData?.data?.data?.standardizedInfo?.standardizedAddress?.countyFips?.toLowerCase() ||
          responseData?.data?.data?.inputAddress?.iso3CountryCode?.toLowerCase() !==
            responseData?.data?.data?.standardizedInfo?.standardizedAddress?.countyFips?.toLowerCase()
        ) {
          yield put(Actions.setIsValidateAddress(true));
          yield put(Actions.setLoader(false));
        } else {
          yield put(Actions.setIsValidateAddress(false));
        }
        pendoTrack(`${CFST_PENDO_DEFAULT_LABEL} | Address api`, {
          status: "Success",
          statusCode: responseData.data.statusCode,
          accountId: accountNumber,
        });
      } else {
        yield put(Actions.setLoader(false));
        yield put(Actions.setIsValidateAddress(false));
        pendoTrack(`${CFST_PENDO_DEFAULT_LABEL} | Address api`, {
          status: "Location not found",
          statusCode: responseData.data.statusCode,
          accountId: accountNumber,
        });
      }
    } else {
      yield put(Actions.setLoader(false));
      yield put(Actions.addressValidationApiCheck(false));
    }
  } catch (e) {
    pendoTrack(`${CFST_PENDO_DEFAULT_LABEL} | Address api`, {
      status: "Failed",
      statusCode: e.response?.data?.statusCode || e.data?.statusCode || e.response?.status,
      accountId: accountNumber,
    });
    yield put(Actions.setLoader(false));
    yield put(Actions.addressValidationApiCheck(false));
  }
}
//addNewContact

const convertJobDuration = (jobDuration) => {
  if (!jobDuration) return "";
  const jobDurationInHours = jobDuration / 60;
  let duration = "";
  if (jobDurationInHours) {
    duration = `Approximately ${jobDurationInHours} ${jobDurationInHours > 1 ? "hours" : "hour"}`;
  }
  return duration;
};

// Helper function to format street address consistently
const formatStreetAddress = (address1, address2) => {
  return address2 ? `${address1}, ${address2}` : address1;
};

// Helper function to format phone number with country code consistently
const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return null;
  const formatted = phoneNumber.startsWith("+") ? phoneNumber : `+1 ${phoneNumber}`;
  return formatted;
};

function* updateCaseOnSchedule({ isScheduleSuccess = true }) {
  let state = yield select((state) => state);
  const { accountNumber } = yield select((state) => state.ACCOUNT);
  let { APIFailure, isScheduleApiFailed, caseSfdcId } = state.TECHNICIANAPPOINTMENT;

  if (APIFailure.isJobFailure || APIFailure.isSlotsFailure || APIFailure.isTransactionFailure) {
    try {
      let payload = yield* buildUpdateCasePayload("job-failure");
      const response = yield api.updateCase(payload, caseSfdcId);
      yield put(Actions.setIsUpdateCaseApiFailed(!(response?.data?.statusCode === "200" && response?.data?.data)));
      pendoTrack(`${CFST_PENDO_DEFAULT_LABEL} | Update case on schedule api`, {
        status: "Success",
        statusCode: response?.data?.statusCode || 200,
        accountId: accountNumber,
      });
    } catch (error) {
      yield put(Actions.setIsUpdateCaseApiFailed(true));
      pendoTrack(`${CFST_PENDO_DEFAULT_LABEL} | Update case on schedule api`, {
        status: "Failed",
        statusCode: error.response?.data?.statusCode || error.data?.statusCode || error.response?.status,
        accountId: accountNumber,
      });
    }
  } else {
    try {
      let payload =
        isScheduleSuccess && !isScheduleApiFailed
          ? yield* buildUpdateCasePayload("schedule-success")
          : yield* buildUpdateCasePayload("schedule-failure");
      const response = yield api.updateCase(payload, caseSfdcId);
      yield put(Actions.setIsUpdateCaseApiFailed(!(response?.data?.statusCode === "200" && response?.data?.data)));
      return response?.data?.statusCode === "200";
    } catch {
      yield put(Actions.setIsUpdateCaseApiFailed(true));
      return false;
    }
  }
}
//fetch an appointment's details
function* getJobDetails({ appointmentId, setLoader }) {
  const { account_id, accountNumber } = yield select((state) => state.ACCOUNT);
  try {
    setLoader(true);

    const payload = {
      accountId: account_id,
    };
    const response = yield api.fetchJobDetails(appointmentId, payload);
    const { data } = response || {};
    const { statusCode, data: jobData } = data || {};
    if (statusCode === "200" && jobData?.id) {
      yield put(Actions.setIsStatus(jobData.status));
      const formattedJobData = yield call(appointmentStatusMapping, [jobData]);
      const [transferDetails, removeDetails] = formattedJobData[0].services;
      const allServiceDetails = [
        ...transferDetails.servicesDetails.map((service) => ({ serviceType: "Transfer", ...service })),
        ...removeDetails.servicesDetails.map((service) => ({ serviceType: "Removal", ...service })),
      ];
      yield put(Actions.setIsWithinThreeBusinessDays(formattedJobData[0].isRescheduleNotAllowed));
      yield put(Actions.setJobDetails({ ...formattedJobData[0], allServiceDetails }));
      yield put(Actions.setJobDetailsApiFailed(false));
      pendoTrack(`${CFST_PENDO_DEFAULT_LABEL} | Job details api`, {
        status: "Success",
        statusCode: statusCode,
        accountId: accountNumber,
      });
    } else if (jobData?.isAccountIdMatch === false) {
      yield put(Actions.setJobDetails({ ...jobData }));
      yield put(Actions.setJobDetailsApiFailed(false));
      pendoTrack(`${CFST_PENDO_DEFAULT_LABEL} | Job details api`, {
        status: "Account doesn't match",
        statusCode: statusCode,
        accountId: accountNumber,
      });
    } else {
      throw new Error("Unable to fetch job details due to an unexpected error");
    }
  } catch (error) {
    yield put(Actions.setJobDetails(null));
    yield put(Actions.setJobDetailsApiFailed(true));
    pendoTrack(`${CFST_PENDO_DEFAULT_LABEL} | Job details api`, {
      status: "Failed",
      statusCode: error.response?.data?.statusCode || error.data?.statusCode || error.response?.status,
      accountId: accountNumber,
    });
  } finally {
    setLoader(false);
  }
}

//Update job details
function* updateJobAPI({ incrementStep }) {
  const { accountNumber } = yield select((state) => state.ACCOUNT);
  try {
    yield put(Actions.setLoader(true));
    let state = yield select((state) => state);
    let id = state.TECHNICIANAPPOINTMENT.jobResponseData?.id;
    const {
      contactFormValues,
      serviceDetails,
      accountDetails,
      jobDurationDetails,
      estAppointmentLength,
      userSelectedContactOption,
      userSelectedAddressOption,
      addressFormValues,
      jobResponseData,
      updateJob,
      caseNumber,
      caseSfdcId,
    } = state.TECHNICIANAPPOINTMENT;
    const { account_id, emailAddress, firstName, surname, isSourcewellGovernment } = state.ACCOUNT;
    const { contractAccount } = state.BILLING;

    const nameData = {
      firstName: firstName,
      surname: surname
    };

    let transferArray = serviceDetails?.filter((item) => item.serviceType === "Transfer");
    let removalArray = serviceDetails?.filter((item) => item.serviceType === "Removal");
    let createJobResponse = Object.keys(updateJob?.payload).length !== 0 ? updateJob?.payload : jobResponseData;
    let jobDetailsData = createJobResponse?.jobDetails ? createJobResponse?.jobDetails[0] : [];
    let isUpdated = checkIfAddressIsUpdated(userSelectedAddressOption, createJobResponse, addressFormValues);

    // Create a custom function to preserve backend order while updating jobDurationDetails
    const preserveBackendOrder = (backendJobDuration, frontendJobDuration) => {
      if (!backendJobDuration || backendJobDuration.length === 0) {
        return getLineIdForJobDurationDetails({}, frontendJobDuration, "add");
      }

      // Start with backend items in their original order
      const result = [];
      const backendMaterialMap = new Map();

      // Map backend items by materialId + processingCode for quick lookup
      backendJobDuration.forEach((item) => {
        const key = `${item.materialId}_${item.processingCode}`;
        backendMaterialMap.set(key, item);
      });

      // Process frontend items to update existing backend items or mark as new
      const frontendMaterialMap = new Map();
      const newFrontendItems = [];

      frontendJobDuration.forEach((frontendItem) => {
        const key = `${frontendItem.materialCode}_${frontendItem.processingCode}`;
        frontendMaterialMap.set(key, frontendItem);

        if (!backendMaterialMap.has(key)) {
          newFrontendItems.push(frontendItem);
        }
      });

      // First, process existing backend items in their original order
      backendJobDuration.forEach((backendItem) => {
        const key = `${backendItem.materialId}_${backendItem.processingCode}`;
        const frontendItem = frontendMaterialMap.get(key);

        if (frontendItem) {
          // Update existing item with frontend data but preserve backend structure
          result.push({
            ...frontendItem,
            id: backendItem.id,
            materialLineId: backendItem.materialLineID,
            // Preserve any other backend-specific fields
          });
        }
      });

      // Then add new items
      let maxId = Math.max(...backendJobDuration.map((item) => Number(item.materialLineID || 0)), 0);
      newFrontendItems.forEach((newItem) => {
        maxId += 10;
        result.push({
          ...newItem,
          materialLineId: maxId.toString().padStart(6, "0"),
        });
      });

      // Apply linked material line ID logic inline
      const resultWithLinkedIds = result.map((data) => {
        if (data.processingCode === "ZSPO") {
          let matchingZBTP = result.find((item) => item.materialCode === data.materialCode && item.processingCode === "ZBTP");
          if (matchingZBTP) data.linkedMaterialLineId = matchingZBTP.materialLineId;
        } else if (data.processingCode === "ZBTP") {
          let matchingZSPO = result.find((item) => item.materialCode === data.materialCode && item.processingCode === "ZSPO");
          if (matchingZSPO) data.linkedMaterialLineId = matchingZSPO.materialLineId;
        }

        // Remove totalFee property from add/update job API payload
        const { totalFee, ...materialLineData } = data;
        return materialLineData;
      });

      return resultWithLinkedIds;
    };

    let newJobDurationDetails = preserveBackendOrder(jobResponseData?.jobDurationDetails, jobDurationDetails);
    let payload = {
      estAppointmentLength: estAppointmentLength,
      status: "OPEN",
      startDate: createJobResponse.startDate,
      appointmentLength: estAppointmentLength / 60,
      ...contactPayload(userSelectedContactOption, contactFormValues),
      accountId: account_id ?? "",
      addressId: isUpdated ? null : createJobResponse.addressId,
      ...addressPayload(userSelectedAddressOption, addressFormValues),
      division: createJobResponse.division,
      distributionChannel: createJobResponse.distributionChannel,
      customerName: createJobResponse.customerName,
      customerNumber: createJobResponse.customerNumber,
      vztSegment: createJobResponse.vztSegment,
      isDeleted: createJobResponse.isDeleted,
      extendedJobId: createJobResponse.extendedJobId,
      scheduleType: createJobResponse.scheduleType,
      scheduleStartTime: "",
      scheduleEndDate: "",
      scheduleEndTime: "",
      erpBusinessPartnerId: accountDetails?.data?.erpPartnerNumber ?? "",
      providerOrder: "",
      erpTicketType: "ZSRQ",
      assignToName: ASSIGNED_TO,
      erpProductRecipientPartyId: accountDetails?.data?.erpPartnerNumber ?? "",
      salesOrg: accountDetails?.data?.salesOrg ?? "",
      resellerBusinessPartnerNumber: accountDetails?.data?.resellerBusinessPartnerNumber ?? "",
      brand: accountDetails?.data?.accountBrand ?? "",
      createdAt: createJobResponse.createdAt,
      createdBy: createJobResponse.createdBy,
      createdByEmail: emailAddress ?? "",
      createdByEid: isSourcewellGovernment ? GOVT_MODIFIED_BY_EID : MODIFIED_BY_EID, //Same for created and modified EID
      modifiedAt: new Date().toISOString(),
      modifiedBy: `${firstName ?? ""} ${surname ?? ""}`,
      modifiedByEmail: emailAddress ?? "",
      modifiedByEid: isSourcewellGovernment ? GOVT_MODIFIED_BY_EID : MODIFIED_BY_EID,
      assignedTo: ASSIGNED_TO,
      assignedToEmail: isSourcewellGovernment ? GOVT_ASSIGNED_TO_EMAIL : ASSIGNED_TO_EMAIL,
      assignedToEid: isSourcewellGovernment ? GOVT_ASSIGNED_EID : ASSIGNED_EID,
      contractAccountId: contractAccount ?? "",
      crmReferenceId: caseNumber ?? "",
      jobDetails: jobDetailsPayload(transferArray, removalArray, caseSfdcId, caseNumber, nameData, jobDetailsData),
      jobDurationDetails: newJobDurationDetails ?? {},
      services: createJobResponse.services ?? [],
    };
    const responseData = yield api.updateJob(payload, id);
    if (responseData?.data?.statusCode === "200" && responseData?.data?.data) {
      // Update cameras in job services based on Redux store's serviceDetails
      const updatedJobResponse = {
        ...responseData.data.data,
        services: updateCamerasInJobServices(responseData.data.data.services, serviceDetails),
      };

      yield put(Actions.setAddJobResp(updatedJobResponse));

      incrementStep();
      pendoTrack(`${CFST_PENDO_DEFAULT_LABEL} | Update job api`, {
        status: "Success",
        statusCode: responseData.data.statusCode,
        accountId: accountNumber,
      });
    }
    yield put(Actions.setLoader(false));
  } catch (error) {
    yield put(Actions.setLoader(false));
    pendoTrack(`${CFST_PENDO_DEFAULT_LABEL} | Update job api`, {
      status: "Failed",
      statusCode: error.response?.data?.statusCode || error.data?.statusCode || error.response?.status,
      accountId: accountNumber,
    });
  }
}

function* fetchRescheduleReasonList({ callback }) {
  const { accountNumber } = yield select((state) => state.ACCOUNT);
  try {
    yield put(Actions.setLoader(true));
    yield put(Actions.setRescheduleReasonList({ reasons: [], error: false }));
    const response = yield api.fetchRescheduleReasonList();
    if (response?.data?.data?.length) {
      let mapReasons = [
        { name: "Unsuitable date/time", value: "Customer Requested Different Date/Time" },
        { name: "Vehicle unavailable", value: "Customer/Vehicle No show" },
        { name: "Site conditions/weather", value: "Site Conditions/Weather" },
        { name: "Vehicle issue", value: "Vehicle Issue" },
        { name: "Other", value: "Other" },
      ];
      let reasons = [];
      const rescheduleReasons = response.data.data;
      mapReasons.forEach((reason) => {
        let reasonMatched = rescheduleReasons.find((item) => reason.value.toLowerCase() === item?.description.toLowerCase());
        if (reasonMatched) {
          reasons.push({
            ...reasonMatched,
            mappedName: reason.name,
          });
        }
      });
      pendoTrack(`${CFST_PENDO_DEFAULT_LABEL} | Reschedule reasons api`, {
        status: "Success",
        statusCode: response.data.statusCode,
        accountId: accountNumber,
      });
      yield put(Actions.setRescheduleReasonList({ reasons: reasons, error: false }));
      callback({ apiFailed: false });
    } else {
      yield put(Actions.setRescheduleReasonList({ reasons: [], error: true }));
      pendoTrack(`${CFST_PENDO_DEFAULT_LABEL} | Reschedule reasons api`, {
        status: "Empty data found",
        statusCode: response.data.statusCode,
        accountId: accountNumber,
      });
      callback({ apiFailed: true });
    }
    yield put(Actions.setLoader(false));
  } catch (error) {
    yield put(Actions.setRescheduleReasonList({ reasons: [], error: true }));
    yield put(Actions.setLoader(false));
    pendoTrack(`${CFST_PENDO_DEFAULT_LABEL} | Reschedule reasons api`, {
      status: "Failed",
      statusCode: error.response?.data?.statusCode || error.data?.statusCode || error.response?.status,
      accountId: accountNumber,
    });
    callback({ apiFailed: true });
  }
}

function* updateCaseOnReschedule({ caseId, scheduleDateTime, isRescheduledAlready, isRescheduleSuccess, errorMessage }) {
  const scheduleDate = moment(scheduleDateTime, "YYYYMMDDHHmm").format("MM/DD/YYYY [at] h:mm A");
  const { accountNumber, isSourcewellGovernment } = yield select((state) => state.ACCOUNT);
  // Destructure jobDetails for payload

  let {
    services,
    email: cfstEmailId,
    name: cfstContactName,
    phone: cfstMobileNumber,
    address1,
    address2,
    city,
    state: stateCode,
    zipCode,
    jobDuration,
    id: appointmentNumber,
    scheduledDate,
    scheduledTime,
    vendorName,
    customerChargeAmount,
    notesToTechnician,
  } = yield select((state) => state.TECHNICIANAPPOINTMENT.jobDetails);

  // Compose street address
  const streetAddress = formatStreetAddress(address1, address2);
  //Previous scheduled date
  const previousAppointmentDateTime = moment([scheduledDate, scheduledTime].map((s) => s?.trim()).join(" "), "MM/DD/YYYY hh:mm A").format(
    "MM/DD/YYYY [at] h:mm A"
  );
  //const previousAppointmentDateTime  = moment(`${scheduledDate} ${scheduledTime}`, "MM/DD/YYYY hh:mm A").format("MM/DD/YYYY [at] h:mm A");
  // Build update case payload
  const basePayload = {
    appointmentNumber: appointmentNumber || "",
    services: mapServiceArrayWithNullChecks(services) || [],
    cfstEmailId,
    cfstContactName,
    cfstMobileNumber: formatPhoneNumber(cfstMobileNumber),
    streetAddress,
    city,
    state: stateCode,
    zipCode,
    duration: convertJobDuration(jobDuration),
    vendorName,
    serviceTotalFees: customerChargeAmount,
    previousAppointmentDateTime,
    additionalNotesToTechnician: notesToTechnician,
  };

  let payload = isRescheduleSuccess
    ? {
        ...basePayload,
        caseOwner: CUSTOMER_PORTAL_INTEGRATION_USER_ID,
        status: "Rescheduled",
        scheduleDate: scheduleDate,
        appointmentConfirmationStatus: "Reschedule Requested",
        rescheduledAlready: isRescheduledAlready,
        reschedule: true,
        reasonMessage: "",
      }
    : {
        ...basePayload,
        caseOwner: isSourcewellGovernment ? GOVT_REVEAL_SUPPORT_QUE_ID : DE_RE_VENDOR_QUE_ID,
        appointmentConfirmationStatus: "Pending Response",
        rescheduledAlready: isRescheduledAlready,
        reschedule: true,
        reasonMessage: errorMessage,
      };
  try {
    yield put(Actions.setLoader(true));
    const response = yield api.updateCase(payload, caseId);
    //return true if updateCase call is success else return false. so that the parent function will decide, wether it move to next stepper
    yield put(Actions.setRescheduleSchedulingEmail(response?.data?.data?.contactEmail || ""));
    yield put(Actions.setIsUpdateCaseApiFailed(false));
    pendoTrack(`${CFST_PENDO_DEFAULT_LABEL} | ${getPageLabel()} | Update case api`, {
      status: "Success",
      statusCode: response.data.statusCode,
      accountId: accountNumber,
    });
    return true;
  } catch (error) {
    pendoTrack(`${CFST_PENDO_DEFAULT_LABEL} | ${getPageLabel()} | Update case api`, {
      status: "Failed",
      statusCode: error.response?.data?.statusCode || error.data?.statusCode || error.response?.status,
      accountId: accountNumber,
    });
    yield put(Actions.setIsUpdateCaseApiFailed(true));
    return false;
  }
}

function* UpdateCaseOnApiFailureReschedule({ callback = null }) {
  try {
    yield put(Actions.setLoader(true));
    let state = yield select((state) => state);
    const { jobDetails, rescheduleAppointmentDetails, APIFailure, isScheduleApiFailed } = state.TECHNICIANAPPOINTMENT;
    const { apptDateAndTime } = rescheduleAppointmentDetails;
    if (apptDateAndTime) {
      yield updateCaseOnReschedule({
        caseId: jobDetails.caseId,
        scheduleDateTime: apptDateAndTime.availableDate + apptDateAndTime.slotStartTime,
        isRescheduledAlready: jobDetails?.status === RESCHEDULED,
        isRescheduleSuccess: false, // Passing false if fetchSlots ot Transaction API fails
        errorMessage: isScheduleApiFailed ? "API Rescheduling Error" : APIFailure.error,
      });
    }
    yield put(Actions.setLoader(false));
    if (callback) {
      callback();
    }
  } catch {
    yield put(Actions.setLoader(false));
    if (callback) {
      callback();
    }
  }
}

function* reschedule({ callBack }) {
  let state = yield select((state) => state);
  const { jobDetails, rescheduleAppointmentDetails } = state.TECHNICIANAPPOINTMENT;
  const { reason, rescheduleReasonCode, apptDateAndTime } = rescheduleAppointmentDetails;
  const { emailAddress, firstName: revealFirstName, surname, accountNumber, isSourcewellGovernment } = state.ACCOUNT;
  try {
    const name = jobDetails?.name.split(" ");
    const firstName = name.length > 1 ? name.slice(0, name.length - 1).join(" ") : name[0];
    const lastName = name.length > 1 ? name[name.length - 1] : "";
    let payload = {
      Header: {
        transactionId: "VZC_" + Date.now(),
        client_name: "VZC",
        isReschedule: true,
      },
      Body: {
        header: {
          clientAppName: "VZC",
          clientAppUserName: "",
          clientAppUserFirstName: firstName,
          clientAppUserLastName: lastName,
          clientAppUserEmail: jobDetails.email,
          clientAppUserRole: "REP",
          isReschedule: true,
        },
        vendorId: apptDateAndTime.vendorId,
        startDate: apptDateAndTime?.availableDate ? moment(apptDateAndTime?.availableDate, "YYYYMMDD").format("MM/DD/YYYY") : "", //"12/04/2024"
        zipcode: jobDetails?.zipCode || "",
        orderNum: jobDetails?.id || "",
        serviceJobId: jobDetails?.serviceJobId || "",
        rescheduleReasonCode: rescheduleReasonCode,
        comments: "",
        rescheduleReason: reason,
        serviceJobType: "Support/De-Re",
        updateBy: "",
        signature: "",
        vehicleCount: jobDetails.allServiceDetails.length,
        createdBy: jobDetails?.createdBy || "",
        apptInfo: {
          installApptSelectedDate: {
            availableDate: apptDateAndTime.availableDate,
            availableDay: apptDateAndTime.availableDay,
            availableType: apptDateAndTime.availableType,
            slotStartTime: apptDateAndTime.slotStartTime,
            slotLength: apptDateAndTime.slotLength,
            durationUnit: apptDateAndTime.durationUnit,
            slotCount: apptDateAndTime.slotCount,
            totalMiles: apptDateAndTime.totalMiles,
            serviceProviderAccount: apptDateAndTime.serviceProviderAccount,
            serviceProviderName: apptDateAndTime.serviceProviderName,
            vendorId: apptDateAndTime.vendorId,
            erpVendorId: apptDateAndTime.erpVendorId,
          },
        },
      },
      modifiedBy: `${revealFirstName} ${surname}`,
      modifiedByEmail: emailAddress,
      modifiedByEid: isSourcewellGovernment ? GOVT_MODIFIED_BY_EID : MODIFIED_BY_EID,
      assignedTo: ASSIGNED_TO,
      assignedToEmail: isSourcewellGovernment ? GOVT_ASSIGNED_TO_EMAIL : ASSIGNED_TO_EMAIL,
      assignedToEid: isSourcewellGovernment ? GOVT_ASSIGNED_EID : ASSIGNED_EID,
    };
    yield put(Actions.setLoader(true));
    const response = yield api.reschedule(payload, jobDetails.id);
    const { statusCode, message } = response?.data || {};
    if (statusCode === "200") {
      pendoTrack(`${CFST_PENDO_DEFAULT_LABEL} | Reschedule api`, {
        status: "Success",
        statusCode: statusCode,
        accountId: accountNumber,
      });
      yield updateCaseOnReschedule({
        caseId: jobDetails.caseId,
        scheduleDateTime: apptDateAndTime.availableDate + apptDateAndTime.slotStartTime,
        isRescheduledAlready: jobDetails?.status === RESCHEDULED,
        isRescheduleSuccess: true,
        errorMessage: "",
      });
      yield put(Actions.setIsScheduleAPIFailed(false));
      callBack();
    } else if (statusCode === "400" && message === "Appointment time unavailable") {
      pendoTrack(`${CFST_PENDO_DEFAULT_LABEL} | Reschedule api`, {
        status: "Appointment time unavailable",
        statusCode: statusCode,
        accountId: accountNumber,
      });
      callBack(true);
    } else {
      throw response;
    }
  } catch (error) {
    pendoTrack(`${CFST_PENDO_DEFAULT_LABEL} | Reschedule api`, {
      status: "Failed",
      statusCode: error?.response?.data?.statusCode || error?.data?.statusCode || error?.response?.status,
      accountId: accountNumber,
    });
    yield updateCaseOnReschedule({
      caseId: jobDetails.caseId,
      scheduleDateTime: apptDateAndTime.availableDate + apptDateAndTime.slotStartTime,
      isRescheduledAlready: jobDetails?.status === RESCHEDULED,
      isRescheduleSuccess: false,
      errorMessage: "API Rescheduling Error",
    });
    yield put(Actions.setIsScheduleAPIFailed(true));
    callBack();
  } finally {
    yield put(Actions.setLoader(false));
  }
}

function mapJobDurationDetailToItem(job) {
  return {
    materialLineId: job?.materialLineID,
    materialId: job?.materialId,
    itemDescription: job?.materialName,
    quantity: job?.quantity,
    unitMeasure: job?.unitOfMeasure,
    techRefId: job?.techRefId,
    processingCode: job?.processingCode,
    lineItemUniqueId: job?.lineItemUniqueId,
    customerPONumber: job?.customerPONumber ?? null,
    customerPONumberKut: null,
    baseItemId: "",
    esnToBeReturned: "",
    contractId: job?.wtMaterialContractId || null,
    sapId: job?.contractCode || null,
    contractName: job?.contractName?.length > 0 ? job?.contractName : null,
    wtMaterialId: job?.wtMaterialId || "",
  };
}

function* createSchedule({ callBack }) {
  let state = yield select((state) => state);
  const { accountDetails, jobResponseData, caseNumber, selectedSlotDetails, notesToTechnician, preferredDate, serviceDetails, materialList } =
    state.TECHNICIANAPPOINTMENT;
  const { emailAddress, firstName, surname, businessPartnerNumber, isSourcewellGovernment } = state.ACCOUNT;
  const { contractAccount } = state.BILLING;
  const { accountNumber } = state.ACCOUNT;
  try {
    let maxMaterialId = max(jobResponseData?.jobDurationDetails?.map((item) => (item.materialLineID ? Number(item.materialLineID) : 0)));
    maxMaterialId += 10;
    const milageLineId = maxMaterialId.toString().padStart(6, "0");
    const milageMaterialData = materialList?.data?.find((item) => item.materialId === "1300000047");
    const name = jobResponseData?.contactName?.split(" ");
    let sessionRandomId = Math.floor(Math.random() * 1000000000);
    let payload = {
      Header: {
        requestControlId: "vzc_" + Date.now(),
        client: "CFST",
        sessionId: sessionRandomId,
      },
      Body: {
        header: {
          clientAppName: "VZC",
          clientAppUserName: "ECOM",
        },
        ignoreCache: false,
        vendorId: selectedSlotDetails?.vendorId,
        startDate: moment(preferredDate).format("MM/DD/YYYY"),
        orderNum: jobResponseData?.id,
        serviceJobType: "INSTALL",
        apptInfo: {
          accountName: `${firstName ?? ""} ${surname ?? ""}`,
          homePhone: jobResponseData?.phone,
          workPhone: "",
          mobilePhone: "",
          primaryPhoneType: "Home",
          emailAddress: jobResponseData.email,
          firstName: name[0],
          lastName: name[1],
          SpecialInstructions: notesToTechnician,
          vendorMessages: null,
          installApptSelectedDate: {
            availableDate: selectedSlotDetails.availableDate,
            availableDay: selectedSlotDetails.availableDay,
            availableType: selectedSlotDetails.availableType,
            slotStartTime: selectedSlotDetails.slotStartTime,
            slotLength: selectedSlotDetails.slotLength,
            durationUnit: selectedSlotDetails.durationUnit,
            slotCount: selectedSlotDetails.slotCount,
            totalMiles: selectedSlotDetails.totalMiles,
            serviceProviderAccount: selectedSlotDetails.serviceProviderAccount,
            serviceProviderName: selectedSlotDetails.serviceProviderName,
            vendorId: selectedSlotDetails.vendorId,
            erpVendorId: selectedSlotDetails.erpVendorId,
          },
        },
        serviceAddress: {
          firstName: name[0],
          lastName: name[1],
          address1: jobResponseData?.address1,
          address2: jobResponseData?.address2,
          city: jobResponseData?.city,
          state: jobResponseData?.stateCode,
          postalCode: jobResponseData?.zipCode,
          country: jobResponseData?.country,
        },
        vehicleInformation: [
          {
            vin: "VIN1243SFDGAGFG1234",
            type: "Truck",
            make: "",
            model: "",
            inventory: [
              {
                name: "VTU1",
                count: "1",
                action: "REMOVE",
              },
              {
                name: "VTU2",
                count: "1",
                action: "INSTALL",
              },
              {
                name: "CAMERA",
                count: "1",
                action: "INSTALL-",
              },
            ],
          },
        ],
        vehicleCount: serviceDetails.length,
        requestControlID: "",
        languagePref: "E",
        mdnPromo: "Y",
        serviceCost: "60.0",
        eventCorrelationId: "",
        launchType: "FCL",
        addressType: "SFU",
        CustomerType: "B",
        workTicketNumber: "",
        documentType: "ZSRQ",
        salesOrg: accountDetails.data.salesOrg,
        currencyCode: accountDetails?.data?.currencyCode,
        division: jobResponseData?.division ?? "",
        distributionChannel: jobResponseData?.distributionChannel ?? "",
        crmReferenceId: caseNumber ?? "",
        contractAccount: contractAccount ?? "",
        erpVendorId: selectedSlotDetails?.erpVendorId,
        salesRepId: isSourcewellGovernment ? GOVT_ASSIGNED_EID : ASSIGNED_EID,
        assignedTo: isSourcewellGovernment ? GOVT_ASSIGNED_EID : ASSIGNED_EID,
        vendorMessage: null,
        soldToParty: businessPartnerNumber,
        shipToParty: businessPartnerNumber,
        subStatus: "",
        oldDeliveryNo: "",
        partners: [
          {
            party: businessPartnerNumber,
            partRoleCode: "AG",
          },
          {
            party: businessPartnerNumber,
            partRoleCode: "WE",
          },
          {
            party: businessPartnerNumber,
            partRoleCode: "Z6",
            accountName: jobResponseData?.customerName,
            contactName: jobResponseData?.contactName,
            contactPhone: jobResponseData?.phone,
            contactEmail: jobResponseData?.email,
            street: jobResponseData?.address1,
            city: jobResponseData?.city,
            state: jobResponseData?.stateCode,
            country: jobResponseData?.country,
            zip: jobResponseData?.zipCode,
            telephone: "",
          },
        ],
        itemDetails: [
        ...(jobResponseData?.jobDurationDetails
          ?.filter((item) => item.processingCode === "ZSPO")
          ?.map(mapJobDurationDetailToItem) || []),
          {
            materialLineId: milageLineId,
            materialId: milageMaterialData?.materialId,
            itemDescription: milageMaterialData?.materialName,
            quantity: Number(selectedSlotDetails?.totalMiles),
            unitMeasure: "",
            techRefId: "",
            processingCode: "ZTML",
            lineItemUniqueId: jobResponseData?.id + "_" + milageLineId,
            customerPONumber: "5000721429",
            customerPONumberKut: null,
            baseItemId: "",
            esnToBeReturned: "",
            contractId: null,
            sapId: null,
            contractName: null,
            wtMaterialId: milageMaterialData?.wtMaterialId,
          },
        ],
      },
      modifiedBy: `${firstName ?? ""} ${surname ?? ""}`,
      modifiedByEmail: emailAddress ?? "",
      modifiedByEid: isSourcewellGovernment ? GOVT_MODIFIED_BY_EID : MODIFIED_BY_EID,
      assignedTo: ASSIGNED_TO,
      assignedToEmail: isSourcewellGovernment ? GOVT_ASSIGNED_TO_EMAIL : ASSIGNED_TO_EMAIL,
      assignedToEid: isSourcewellGovernment ? GOVT_ASSIGNED_EID : ASSIGNED_EID,
    };
    yield put(Actions.setLoader(true));
    const response = yield api.schedule(payload, jobResponseData?.id);
    const { statusCode, message } = response?.data || {};
    if (statusCode === "200") {
      yield updateCaseOnSchedule({ isScheduleSuccess: true });
      pendoTrack(`${CFST_PENDO_DEFAULT_LABEL} | Schedule api`, {
        status: "Success",
        statusCode: statusCode,
        accountId: accountNumber,
      });
      callBack();
    } else if (statusCode === "400" && message === "Appointment time unavailable") {
      callBack(true);
      pendoTrack(`${CFST_PENDO_DEFAULT_LABEL} | Schedule api`, {
        status: "Appointment time unavailable",
        statusCode: statusCode,
        accountId: accountNumber,
      });
    } else {
      throw response;
    }
  } catch (error) {
    yield put(Actions.setIsScheduleAPIFailed(true));
    yield updateCaseOnSchedule({ isScheduleSuccess: false });
    pendoTrack(`${CFST_PENDO_DEFAULT_LABEL} | Schedule api`, {
      status: "Failed",
      statusCode: error?.response?.data?.statusCode || error?.data?.statusCode || error?.response?.status,
      accountId: accountNumber,
    });
    callBack();
  } finally {
    yield put(Actions.setLoader(false));
  }
}

function getVehicleAssetName(isVin, isPoweredAsset, vehicleName) {
  if (isVin) {
    return "Vehicle: " + vehicleName;
  }
  if (isPoweredAsset) {
    return "Powered asset: " + vehicleName;
  }
  return "Non-powered asset: " + vehicleName;
}

function* fetchVehicleAndAssetList({ toLoad }) {
  const { accountNumber } = yield select((state) => state.ACCOUNT);
  try {
    if (toLoad) {
      yield put(Actions.setLoader(true));
    }

    let response;
    response = isEnvQA()
      ? {
          data: {
            statusCode: "200",
            data: VEHICLES_lIST.items,
          },
        }
      : yield api.fetchVehicleAndAssetList();

    const { data: { statusCode, data: vehicleAndAssetList = [] } = {} } = response;
    if (statusCode === "200" && vehicleAndAssetList?.length) {
      const vehiclePoweredAssetList = vehicleAndAssetList.map((item) => {
        let isVin = item.classification.toLowerCase() === "vehicle (default)",
          isPoweredAsset = item.classification.toLowerCase() === "powered asset",
          vehicleName = item.label,
          vehicleAssetName = getVehicleAssetName(isVin, isPoweredAsset, vehicleName),
          esn = "ESN: " + item.imei,
          registrationNumber = "Registration number: " + item.registrationNumber,
          label = `${vehicleAssetName}, ${esn}, ${registrationNumber}`;
        return {
          value: `${item.label} - ${item.imei} - ${item.registrationNumber}`,
          label,
          vehicleName,
          vehicleNumber: item.vehicleNumber,
          vehicleId: item.vehicleId,
          esn: item.imei,
          vin: item.vin,
          registrationNumber: item.registrationNumber,
          isVin,
          isPoweredAsset,
          isOEM: item.boxName.toLowerCase() === "gm" || item.boxName.toLowerCase() === "fordtmc",
          assetName: item.label,
          assetNumber: item.vehicleNumber,
          newVin: null,
          lastPingDate: item.lastPingDate,
          boxName: item.boxName,
          boxModel: "",
          isBoxNameTypeCompatible: false,
          boxModelFetchedVin: "",
          cameras: [],
          noCameraReason: "",
          camerasFetchedEsn: "",
        };
      });

      yield put(Actions.setVehicleAndAssetList({ vehicles: [...vehiclePoweredAssetList], error: false }));
      pendoTrack(`${CFST_PENDO_DEFAULT_LABEL} | Vehicles api`, {
        status: "Success",
        statusCode: response.data.statusCode,
        accountId: accountNumber,
      });
    } else {
      throw new Error("Unable to fetch vehicle and asset list due to an unexpected error");
    }
  } catch (error) {
    yield put(Actions.setVehicleAndAssetList({ vehicles: [], error: true }));
    pendoTrack(`${CFST_PENDO_DEFAULT_LABEL} | Vehicles api`, {
      status: "Failed",
      statusCode: error.response?.data?.statusCode || error.data?.statusCode || error.response?.status,
      accountId: accountNumber,
    });
  } finally {
    if (toLoad) {
      yield put(Actions.setLoader(false));
    }
  }
}

function* addJobAPI({ caseSfdcId, caseNumber }) {
  let state = yield select((state) => state);
  const {
    contactFormValues,
    serviceDetails,
    accountDetails,
    jobDurationDetails,
    estAppointmentLength,
    userSelectedContactOption,
    userSelectedAddressOption,
    addressFormValues,
  } = state.TECHNICIANAPPOINTMENT;
  const { account_id, emailAddress, firstName, surname, accountNumber, erpBusinessPartner, isSourcewellGovernment } = state.ACCOUNT;
  const { contractAccount } = state.BILLING;

  const nameData = {
    firstName: firstName,
    surname: surname
  };

  try {
    let transferArray = serviceDetails?.filter((item) => item.serviceType === "Transfer");
    let removalArray = serviceDetails?.filter((item) => item.serviceType === "Removal");
    let newJobDurationDetails = getLineIdForJobDurationDetails({}, jobDurationDetails, "add");
    let services = servicesArray(serviceDetails);
    let requestBody = {
      status: "OPEN",
      startDate: null,
      appointmentLength: (estAppointmentLength ?? 0) / 60, //Job duration in hours
      estAppointmentLength: estAppointmentLength ?? 0, // Job duration in minutes
      ...contactPayload(userSelectedContactOption, contactFormValues),
      accountId: account_id ?? "",
      ...addressPayload(userSelectedAddressOption, addressFormValues),
      division: accountDetails?.data?.division ?? "",
      distributionChannel: accountDetails?.data?.distributionChannel ?? "",
      customerName: erpBusinessPartner?.data?.companyName ?? "",
      customerNumber: accountNumber ?? "",
      vztSegment: accountDetails?.data?.vzcSegement ?? "",
      isDeleted: false,
      erpLastModifiedAt: null,
      extendedJobId: null,
      scheduleType: "AUTO",
      scheduleStartTime: "",
      scheduleEndDate: "",
      scheduleEndTime: "",
      erpBusinessPartnerId: accountDetails?.data?.erpPartnerNumber ?? "",
      providerOrder: "",
      salesOrg: accountDetails?.data?.salesOrg ?? "",
      resellerBusinessPartnerNumber: accountDetails?.data?.resellerBusinessPartnerNumber ?? "",
      erpTicketType: "ZSRQ",
      assignToName: ASSIGNED_TO,
      erpProductRecipientPartyId: accountDetails?.data?.erpPartnerNumber ?? "",
      brand: accountDetails?.data?.accountBrand ?? "",
      createdAt: new Date().toISOString(),
      // createdBy: `${firstName ?? ""} ${surname ?? ""}`,
      // createdByEmail: emailAddress ?? "",
      createdBy:
        contactFormValues?.existingContact?.firstName && contactFormValues?.existingContact?.lastName
          ? `${contactFormValues.existingContact.firstName} ${contactFormValues.existingContact.lastName}`
          : `${firstName ?? ""} ${surname ?? ""}`,
      createdByEmail: contactFormValues?.existingContact?.email || (emailAddress ?? ""),
      createdByEid: isSourcewellGovernment ? GOVT_MODIFIED_BY_EID : MODIFIED_BY_EID, //Same for created and modified EID
      modifiedAt: new Date().toISOString(),
      modifiedBy: null,
      modifiedByEmail: null,
      modifiedByEid: isSourcewellGovernment ? GOVT_MODIFIED_BY_EID : MODIFIED_BY_EID,
      assignedTo: ASSIGNED_TO,
      assignedToEmail: isSourcewellGovernment ? GOVT_ASSIGNED_TO_EMAIL : ASSIGNED_TO_EMAIL,
      assignedToEid: isSourcewellGovernment ? GOVT_ASSIGNED_EID : ASSIGNED_EID,
      jobDetails: jobDetailsPayload(transferArray, removalArray, caseSfdcId, caseNumber, nameData, null, contactFormValues),
      contractAccountId: contractAccount ?? "",
      crmReferenceId: caseNumber ?? "",
      jobDurationDetails: newJobDurationDetails ?? {},
      services: services ?? [],
    };

    const response = yield api.addJob(requestBody);
    if (response.data.statusCode === "200") {
      // Update cameras in job services based on Redux store's serviceDetails
      const updatedJobResponse = {
        ...response.data.data,
        services: updateCamerasInJobServices(response.data.data.services, serviceDetails),
      };

      yield put(Actions.setAddJobResp(updatedJobResponse));

      yield put(Actions.setAPIFailure({ isJobFailure: false, error: "", isSlotsFailure: false, isTransactionFailure: false }));
      pendoTrack(`${CFST_PENDO_DEFAULT_LABEL} | Add job api`, {
        status: "Success",
        statusCode: response.data.statusCode,
        accountId: accountNumber,
      });
      return true;
    } else {
      yield put(
        Actions.setAPIFailure({ isJobFailure: true, error: "C360 Job Creation is failed", isSlotsFailure: false, isTransactionFailure: false })
      );
      pendoTrack(`${CFST_PENDO_DEFAULT_LABEL} | Add job api`, {
        status: "Failed",
        statusCode: response.data.statusCode,
        accountId: accountNumber,
      });
      return false;
    }
  } catch (err) {
    yield put(
      Actions.setAPIFailure({ isJobFailure: true, error: "C360 Job Creation is failed", isSlotsFailure: false, isTransactionFailure: false })
    );
    pendoTrack(`${CFST_PENDO_DEFAULT_LABEL} | Add job api`, {
      status: "Failed",
      statusCode: err.response?.data?.statusCode || err.data?.statusCode || err.response?.status,
      accountId: accountNumber,
    });
    return false;
  }
}

function* createNewCaseAPI({ incrementStep }) {
  const state = yield select((state) => state);
  try {
    const reqBody = buildRequestBody(state);
    yield put(Actions.setLoader(true));

    const response = yield api.createNewCase(reqBody);
    yield handleCreateCaseResponse(response, state, incrementStep);
  } catch (err) {
    yield handleCreateCaseError(err, state.ACCOUNT.accountNumber);
  } finally {
    yield put(Actions.setLoader(false));
  }
}

function getOldVinBoxType(serviceDetails) {
  const incompatible = serviceDetails.find((item) => item.serviceType === "Transfer" && item.isCompatible === false);
  if (!incompatible) return "";
  const boxName = incompatible.selectedVinOrPA?.boxName || "";
  if (boxName?.toLowerCase().includes("calamp")) return "Calamp";
  return incompatible.warningReason || "CABLES_DID_NOT_MATCH";
}

function getContactDetails(contactFormValues, userSelectedContactOption, account) {
  const existing = contactFormValues?.existingContact || {};

  // Determine phone number with simplified logic
  const getPhoneNumber = () => {
    const mobileNumber = existing.mobileNumber;
    const accountPhone = account.phoneNumber;

    if (mobileNumber) {
      return formatPhoneNumber(mobileNumber);
    }

    if (accountPhone) {
      return formatPhoneNumber(accountPhone);
    }

    return null;
  };

  return {
    emailId: existing.email || account.emailAddress,
    phoneNumber: getPhoneNumber(),
    firstName: existing.firstName || account.firstName,
    lastName: existing.lastName || account.surname,
  };
}

function getAssistanceFields(isAssistanceRequired, isJobDurationExceeds10Hrs, contact, address, estAppointmentLength) {
  if (requiresAssistance(isAssistanceRequired, isJobDurationExceeds10Hrs)) {
    return {
      cfstEmailId: contact.email,
      cfstContactName: contact.contactName,
      cfstMobileNumber: formatPhoneNumber(contact.phone),
      streetAddress: formatStreetAddress(address.address1, address.address2),
      city: address.city,
      state: address.stateCode,
      zipCode: address.zipCode,
      duration: convertJobDuration(estAppointmentLength),
    };
  }
  return {};
}

function getReasonMessage(isAssistanceRequired, isJobDurationExceeds10Hrs) {
  if (isAssistanceRequired) return "Issue with Hardware compatibility";
  if (isJobDurationExceeds10Hrs) return "Job Duration is more than 10hours";
  return "";
}

function requiresAssistance(isAssistanceRequired, isJobDurationExceeds10Hrs) {
  return isAssistanceRequired || isJobDurationExceeds10Hrs;
}

function buildRequestBody(state) {
  const {
    serviceDetails,
    isAssistanceRequired,
    isJobDurationExceeds10Hrs,
    userSelectedContactOption,
    contactFormValues,
    userSelectedAddressOption,
    addressFormValues,
    estAppointmentLength,
    notesToTechnician,
  } = state.TECHNICIANAPPOINTMENT;
  const contact = contactPayload(userSelectedContactOption, contactFormValues);
  const address = addressPayload(userSelectedAddressOption, addressFormValues);
  const { account_id, isSourcewellGovernment } = state.ACCOUNT;
  const transferCount = serviceDetails.filter((item) => item.serviceType === "Transfer").length;
  const removalCount = serviceDetails.filter((item) => item.serviceType === "Removal").length;
  const subject = caseSubjectField(transferCount, removalCount);
  const oldVinBoxType = getOldVinBoxType(serviceDetails);
  const contactDetails = getContactDetails(contactFormValues, userSelectedContactOption, state.ACCOUNT);
  const assistanceFields = getAssistanceFields(isAssistanceRequired, isJobDurationExceeds10Hrs, contact, address, estAppointmentLength);
  const reasonMessage = getReasonMessage(isAssistanceRequired, isJobDurationExceeds10Hrs);
  const needsAssistance = requiresAssistance(isAssistanceRequired, isJobDurationExceeds10Hrs);
  const services = servicesArray(serviceDetails);
  const vehicleDetails = getVehicleDetailsFromServices(services);
  const vehicleLabels = vehicleDetails.map((item) => item.vehicleName)?.join(", ");
  const registrationNumbers = vehicleDetails.map((item) => item.registrationNumber)?.join(", ");
  const revealCoreESNs = vehicleDetails.map((item) => item.revealCoreESN)?.join(", ");
  // Extract caseOwner value to avoid nested ternary
  let caseOwnerValue;
  if (isAssistanceRequired || isJobDurationExceeds10Hrs) {
    caseOwnerValue = isSourcewellGovernment ? GOVT_REVEAL_SUPPORT_QUE_ID : DE_RE_VENDOR_QUE_ID;
  } else {
    caseOwnerValue = CUSTOMER_PORTAL_INTEGRATION_USER_ID;
  }

  return {
    accountId: account_id,
    subject: subject,
    country: "US",
    status: "New",
    origin: "VZC-Customer Portal",
    reason: "Hardware",
    specificReason: "De-install / Re-install",
    caseOwner: caseOwnerValue,
    ...contactDetails,
    productFamily: "Reveal",
    initialInquiry: "Appointment request created online",
    appointmentConfirmationStatus: needsAssistance ? "Pending Response" : "",
    oldVinBoxType: oldVinBoxType,
    ...assistanceFields,
    reasonMessage,
    reschedule: false,
    services: mapServiceArrayWithNullChecks(servicesArray(serviceDetails)) || [],
    vehicleLabel: vehicleLabels,
    esn: revealCoreESNs,
    vinRegistrationNo: registrationNumbers,
    additionalNotesToTechnician: notesToTechnician || "",
  };
}

function* handleCreateCaseResponse(response, state, incrementStep) {
  const { accountNumber } = state.ACCOUNT;

  if (response.data.statusCode === "200") {
    yield put(Actions.setCreateCaseAPIFailure(null));
    const { id: caseId = null, caseNumber = null } = response?.data?.data || {};

    if (state.TECHNICIANAPPOINTMENT.isAssistanceRequired) {
      yield put(Actions.setAssistanceCaseDetails({ caseId, caseNumber }));
      incrementStep();
    } else {
      yield put(Actions.setCaseNumber(caseNumber || ""));
      yield put(Actions.setCaseSfdcId(caseId || ""));
      if (yield addJobAPI({ caseSfdcId: caseId, caseNumber: caseNumber })) incrementStep();
    }

    pendoTrack(`${CFST_PENDO_DEFAULT_LABEL} | Create case api`, {
      status: "Success",
      statusCode: response.data.statusCode,
      accountId: accountNumber,
    });
  } else {
    yield put(Actions.setCreateCaseAPIFailure(true));
    pendoTrack(`${CFST_PENDO_DEFAULT_LABEL} | Create case api`, {
      status: "Failed",
      statusCode: response.data.statusCode,
      accountId: accountNumber,
    });
  }
}

function* handleCreateCaseError(err, accountNumber) {
  yield put(Actions.setCaseSfdcId(""));
  yield put(Actions.setCreateCaseAPIFailure(true));
  console.error("Error in createNewCaseAPI:", err);
  pendoTrack(`${CFST_PENDO_DEFAULT_LABEL} | Create case api`, {
    status: "Failed",
    statusCode: err.response?.data?.statusCode || err.data?.statusCode || err.response?.status,
    accountId: accountNumber,
  });
}

function* closeCaseAndJobAPI({ callBack }) {
  let state = yield select((state) => state);
  const { jobResponseData, caseSfdcId } = state.TECHNICIANAPPOINTMENT;
  const { firstName, surname, accountNumber } = state.ACCOUNT;
  try {
    yield put(Actions.setLoader(true));
    let reqBody = {
      caseId: caseSfdcId || "",
      jobId: jobResponseData?.id ? jobResponseData?.id : "",
      modifiedByEid: MODIFIED_BY_EID,
      modifiedBy: `${firstName ?? ""} ${surname ?? ""}`,
    };
    let response = yield api.closeCaseAndJob(reqBody);
    if (response.data.statusCode === "200") {
      yield put(Actions.setLoader(false));
      yield put(Actions.setServiceTotalFee(0));
      yield put(Actions.resetAppointmentDetails());
      pendoTrack(`${CFST_PENDO_DEFAULT_LABEL} | Close case and job api`, {
        status: "Success",
        statusCode: response.data.statusCode,
        accountId: accountNumber,
      });
      callBack();
    }
  } catch (err) {
    yield put(Actions.setLoader(false));
    yield put(Actions.setServiceTotalFee(0));
    yield put(Actions.resetAppointmentDetails());
    pendoTrack(`${CFST_PENDO_DEFAULT_LABEL} | Close case and job api`, {
      status: "Failed",
      statusCode: err.response?.data?.statusCode || err.data?.statusCode || err.response?.status,
      accountId: accountNumber,
    });
    callBack();
  }
}

//function to fetch the boxtype from SL and check if the boxtype is compatible with the boxname from reveal
function* getVinFeatures({ vin, boxName, callBack }) {
  const { accountNumber } = yield select((state) => state.ACCOUNT);
  try {
    yield put(Actions.setAccessoriesLoader(true));
    const requestBody = {
      vin,
    };
    const response = yield api.getFeaturesByVin(requestBody);
    const { data: { statusCode, data: vinData = [] } = {} } = response;
    if (statusCode === "200" && vinData.length) {
      const boxModel = vinData[0].BoxModel ?? "",
        isCompatible = vinData[0].BoxType === boxName;
      callBack(boxModel, isCompatible);
      pendoTrack(`${CFST_PENDO_DEFAULT_LABEL} | VinFeatures api`, {
        status: "Success",
        statusCode: statusCode,
        accountId: accountNumber,
      });
    } else {
      pendoTrack(`${CFST_PENDO_DEFAULT_LABEL} | VinFeatures api`, {
        status: "Empty data",
        statusCode: statusCode,
        accountId: accountNumber,
      });
      callBack("", false);
    }
  } catch (error) {
    callBack("", false, true);
    yield put(Actions.setAccessoriesLoader(false));
    pendoTrack(`${CFST_PENDO_DEFAULT_LABEL} | VinFeatures api`, {
      status: "Failed",
      statusCode: error.response?.data?.statusCode || error.data?.statusCode || error.response?.status,
      accountId: accountNumber,
    });
  }
}

//function to fetch the cameras for the selected ESN
function* getVinControlUnits({ esn, callBack, toLoad }) {
  const universalAccountId = yield select((state) => state.ACCOUNT.accountNumber);
  try {
    if (toLoad) {
      yield put(Actions.setAccessoriesLoader(true));
    }
    const requestBody = buildVinControlUnitsReq(universalAccountId, esn);
    const response = yield api.getVinControlUnits(requestBody);
    handleVinControlUnitsResponse(response, universalAccountId, callBack);
  } catch (error) {
    handleVinControlUnitsError(error, universalAccountId, callBack);
  } finally {
    yield put(Actions.setAccessoriesLoader(false));
  }
}

function buildVinControlUnitsReq(universalAccountId, esn) {
  return {
    universalAccountId: isEnvQA() ? 1001925651 : universalAccountId,
    vehicleEsn: esn,
  };
}

function handleVinControlUnitsResponse(response, universalAccountId, callBack) {
  const {
    data: {
      statusCode,
      data: { Items: controlUnits = [] },
    },
  } = response;
  let cameras = [];
  if (statusCode === "200") {
    if (controlUnits?.length) {
      if (controlUnits.some((unit) => unit.DeviceFamily === "CP4")) {
        trackEvcDetected(universalAccountId);
        callBack(cameras, "EVC_DETECTED");
      } else {
        handleControlUnits(controlUnits, cameras, callBack);
      }
    } else {
      callBack(cameras, "No cameras installed in this vehicle");
    }
  } else {
    trackNullResponse(universalAccountId, statusCode);
    callBack(cameras, "Unable to fetch vehicle control units due to an unexpected error");
  }
}

function handleControlUnits(controlUnits, cameras, callBack) {
  for (const controlUnit of controlUnits) {
    const { Cameras } = controlUnit;
    for (const camera of Cameras) {
      cameras.push({ name: camera.CameraFunction, esn: camera.SerialNumber });
    }
  }
  callBack(cameras, "");
}

function trackEvcDetected(universalAccountId) {
  pendoTrack(`${CFST_PENDO_DEFAULT_LABEL} | Camera api`, {
    status: "EVC_DETECTED",
    accountId: universalAccountId,
  });
}

function trackNullResponse(universalAccountId, statusCode) {
  pendoTrack(`${CFST_PENDO_DEFAULT_LABEL} | Camera api`, {
    status: "Null response",
    statusCode: statusCode,
    accountId: universalAccountId,
  });
}

function handleVinControlUnitsError(error, universalAccountId, callBack) {
  pendoTrack(`${CFST_PENDO_DEFAULT_LABEL} | Camera api`, {
    status: "Failed",
    statusCode: error.response?.data?.statusCode || error.data?.statusCode || error.response?.status,
    accountId: universalAccountId,
  });
  callBack([], "Unable to fetch vehicle control units due to an unexpected error", true);
}

function* buildUpdateCasePayload(type) {
  let state = yield select((state) => state);
  const { serviceDetails, jobResponseData, notesToTechnician, selectedSlotDetails, isReschedule, estAppointmentLength, APIFailure } =
    state.TECHNICIANAPPOINTMENT;
  const { isSourcewellGovernment } = state.ACCOUNT;
  const services = servicesArray(serviceDetails);
  const transferCount = serviceDetails.filter((item) => item.serviceType === "Transfer")?.length || 0;
  const removalCount = serviceDetails.filter((item) => item.serviceType === "Removal")?.length || 0;
  const subject = caseSubjectField(transferCount, removalCount);
  const vehicleDetails = getVehicleDetailsFromServices(jobResponseData?.services || services);
  const vehicleLabels = vehicleDetails.map((item) => item.vehicleName)?.join(", ");
  const registrationNumbers = vehicleDetails.map((item) => item.registrationNumber)?.join(", ");
  const revealCoreESNs = vehicleDetails.map((item) => item.revealCoreESN)?.join(", ");
  const formattedScheduledDate = moment(selectedSlotDetails?.availableDate + selectedSlotDetails?.slotStartTime, "YYYYMMDDHHmm").format(
    "MM/DD/YYYY [at] h:mm A"
  );

  const formattedServicesArray = mapServiceArrayWithNullChecks(services);
  // Common base payload for all cases
  const basePayload = {
    subject,
    vehicleLabel: vehicleLabels,
    esn: revealCoreESNs,
    vinRegistrationNo: registrationNumbers,
    services: formattedServicesArray,
    reschedule: isReschedule,
    serviceTotalFees: jobResponseData?.estimateCustomerFee ?? 0,
    additionalNotesToTechnician: notesToTechnician || "",
    appointmentNumber: jobResponseData?.id || "",
  };
  const contactAddress = getContactAddressPayload(state);

  switch (type) {
    case "schedule-success": {
      return {
        ...basePayload,
        ...contactAddress,
        ...{
          status: "Scheduled",
          scheduleDate: formattedScheduledDate,
          origin: "VZC-Customer Portal",
          vendor: selectedSlotDetails?.erpVendorId || "",
          vendorName: selectedSlotDetails?.vendorName || "",
          duration: convertJobDuration(jobResponseData?.estAppointmentLength),
          appointmentConfirmationStatus: "Confirmed",
          reasonMessage: "",
        },
      };
    }
    case "schedule-failure": {
      return {
        ...basePayload,
        ...contactAddress,
        ...{
          caseOwner: isSourcewellGovernment ? GOVT_REVEAL_SUPPORT_QUE_ID : DE_RE_VENDOR_QUE_ID,
          appointmentConfirmationStatus: "Pending Response",
          duration: convertJobDuration(jobResponseData?.estAppointmentLength),
          reasonMessage: "API Scheduling Error",
        },
      };
    }
    case "job-failure":
    case "transaction-api-failure":
    case "slots-api-failure": {
      return {
        ...basePayload,
        ...contactAddress,
        ...{
          origin: "VZC-Customer Portal",
          caseOwner: isSourcewellGovernment ? GOVT_REVEAL_SUPPORT_QUE_ID : DE_RE_VENDOR_QUE_ID,
          appointmentConfirmationStatus: "Pending Response",
          duration: convertJobDuration(estAppointmentLength),
          reasonMessage: APIFailure?.error,
        },
      };
    }
    default:
      return {};
  }
}

// Helper to build vehicle details from services
function getVehicleDetailsFromServices(services) {
  return (
    services?.flatMap((service) =>
      service.servicesDetails.map((details) => ({
        vehicleName: details?.existingVehicle?.vehicleName,
        registrationNumber: details?.existingVehicle?.registrationNumber,
        revealCoreESN: details?.existingVehicle?.revealCoreESN,
        serviceType: service.type,
        newVehicle: details?.newVehicle ? { ...details?.newVehicle } : undefined,
        accessories: details.relatedServices,
        vin: details?.existingVehicle?.vin,
      }))
    ) || []
  );
}

// Helper to build contact/address payload
function getContactAddressPayload(state) {
  const { contactFormValues, userSelectedContactOption, userSelectedAddressOption, addressFormValues } = state.TECHNICIANAPPOINTMENT;
  const { email, contactName, phone } = contactPayload(userSelectedContactOption, contactFormValues);
  const { address1, address2, city, stateCode, zipCode } = addressPayload(userSelectedAddressOption, addressFormValues);
  const streetAddress = formatStreetAddress(address1, address2);
  return {
    cfstEmailId: email,
    cfstContactName: contactName,
    cfstMobileNumber: formatPhoneNumber(phone),
    streetAddress,
    city,
    state: stateCode,
    zipCode,
  };
}

function mapServiceArrayWithNullChecks(input) {
  return (input || []).map((service) => ({
    type: service?.type ?? null,
    servicesDetails: (service?.servicesDetails || [])
      .filter((detail) => detail.isDeleted !== true)
      .map((detail) => ({
        existingVehicle: {
          currentVehicleVin: detail?.existingVehicle?.vin ?? null,
          currentVehicleLabel: detail?.existingVehicle?.vehicleName ?? null,
          currentRegistrationNo: detail?.existingVehicle?.registrationNumber ?? null,
          isVin: detail?.existingVehicle?.isVin ?? null,
          cameras: detail?.existingVehicle?.cameras ?? [],
        },
        newVehicle: {
          id: detail?.newVehicle?.id ?? null,
          newVehicleVin: detail?.newVehicle?.vin ?? null,
          newVehicleLabel: detail?.newVehicle?.vehicleName ?? null,
          newRegistrationNo: detail?.newVehicle?.registrationNumber ?? null,
        },
        accessories: detail?.relatedServices ?? null,
      })),
  }));
}

// Helper function to update cameras and isVin flag in job services based on uiIndex
const updateCamerasInJobServices = (jobResponseServices, serviceDetailsFromStore) => {
  if (!jobResponseServices || !serviceDetailsFromStore) return jobResponseServices;

  // Create a map of uiIndex to cameras and isVin flag for quick lookup
  const uiIndexToDataMap = {};
  serviceDetailsFromStore.forEach((service) => {
    if (service.uiIndex && service.selectedVinOrPA) {
      uiIndexToDataMap[service.uiIndex] = {
        cameras: service.selectedVinOrPA.cameras || [],
        isVin: service.selectedVinOrPA.isVin,
      };
    }
  });

  // Update the job response services
  const updatedServices = jobResponseServices.map((serviceType) => {
    const updatedServiceDetails = serviceType.servicesDetails.map((serviceDetail) => {
      if (serviceDetail.uiIndex && uiIndexToDataMap[serviceDetail.uiIndex]) {
        const { cameras, isVin } = uiIndexToDataMap[serviceDetail.uiIndex];
        return {
          ...serviceDetail,
          existingVehicle: {
            ...serviceDetail.existingVehicle,
            cameras: cameras,
            isVin: isVin,
          },
        };
      }
      return serviceDetail;
    });

    return {
      ...serviceType,
      servicesDetails: updatedServiceDetails,
    };
  });

  return updatedServices;
};

export default function* technicianAppointment() {
  yield all([takeLatest(Actions.FETCH_FEE, fetchFee)]);
  yield all([takeLatest(Actions.VALIDATE_NEW_VIN, validateVin)]);
  yield all([takeLatest(Actions.CHECK_VIN_COMPATIBILITY, checkVinCompatibility)]);
  yield all([takeLatest(Actions.FETCH_STATES, fetchStates)]);
  yield all([takeLatest(Actions.FETCH_ACCOUNT_DETAILS, fetchAccountDetails)]);
  yield all([takeLatest(Actions.FETCH_MATERIAL_LIST, fetchMaterialList)]);
  yield all([takeLatest(Actions.GET_ALL_APPOINTMENT, getAllAppointment)]);
  yield all([takeLatest(Actions.TECHNICIAN_APPOINTMENT_VALIDATE_ADDRESS, technicianAppointmentValidateAddressData)]);
  yield all([takeLatest(Actions.UPDATE_CASE_DETAILS, updateCaseOnSchedule)]);
  yield all([takeLatest(Actions.GET_JOB_DETAILS, getJobDetails)]);
  yield all([takeLatest(Actions.UPDATE_JOB, updateJobAPI)]);
  yield all([takeLatest(Actions.CREATE_SCHEDULE, createSchedule)]);
  yield all([takeLatest(Actions.FETCH_RESCHEDULE_REASON_LIST, fetchRescheduleReasonList)]);
  yield all([takeLatest(Actions.CREATE_RESCHEDULE, reschedule)]);
  yield all([takeLatest(Actions.FETCH_REVEAL_VEHICLES, fetchVehicleAndAssetList)]);
  yield all([takeLatest(Actions.SCHEDULE_CREATE_NEW_CASE, createNewCaseAPI)]);
  yield all([takeLatest(Actions.CLOSE_CASE_AND_JOB, closeCaseAndJobAPI)]);
  yield all([takeLatest(Actions.GET_VIN_FEATURES, getVinFeatures)]);
  yield all([takeLatest(Actions.GET_VIN_CONTROL_UNITS, getVinControlUnits)]);
  yield all([takeLatest(Actions.UPDATE_CASE_ON_API_FAILURE_RESCHEDULE, UpdateCaseOnApiFailureReschedule)]);
}
